import { useState } from "react";

import { Link } from "react-router-dom";
import { useToasts } from "react-toast-notifications";

import { usePrettyLogEventPage } from "@components/v2/analytics";
import { EXPORT_LANDING_PAGE_ANALYTICS_TABLE_DATA } from "@gql/landing-page-analytics";
import { useNorthbeamMutation } from "@hooks/http";
import {
  type ExportLandingPageAnalyticsTableData,
  type ExportLandingPageAnalyticsTableDataVariables,
} from "@north-beam/nb-client-side";
import { PrimaryButton } from "@shared/buttons";
import { Modal } from "@shared/modal";
import Spinner from "@shared/spinner";
import { TextInput } from "@shared/text-input";

import { useLandingPageAnalyticsContext } from "../hooks";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const ExportTableModal = ({ isOpen, onClose }: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { addToast } = useToasts();

  const {
    filters: {
      attributionModel,
      attributionWindow,
      accountingMode,
      dateRange,
      breakdownFilters,
      level,
      adQuery,
      urlQuery,
      advancedSearch,
    },
    reportTableVariables,
  } = useLandingPageAnalyticsContext();

  const [fileName, setFileName] = useState("landing_page_analytics_data");

  const pageContext = {
    attributionModel,
    attributionWindow,
    accountingMode,
    level,
    dateRange,
    breakdownFilters,
    adQuery,
    urlQuery,
    advancedSearch,
  };

  const [exportData, { loading }] = useNorthbeamMutation<
    ExportLandingPageAnalyticsTableData,
    ExportLandingPageAnalyticsTableDataVariables
  >(EXPORT_LANDING_PAGE_ANALYTICS_TABLE_DATA, { useCpuIntensive: true });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      titleContent={
        <div className="text-lg font-semibold">
          <i
            className="fa-regular fa-sharp fa-download mr-5"
            style={{ fontSize: "1.5em" }}
          />
          Export your Landing Page Analytics Data
        </div>
      }
    >
      <div className="mt-4">
        Your landing page analytics data export will be based on the filters
        that you have applied on the page.
      </div>
      <div className="mt-4">
        Your export may take up to 1 hour to complete depending on the size of
        the export.
      </div>
      <div className="mt-4">
        Exports can be found in the{" "}
        <Link to="../customers/documents" target="_blank" rel="noreferrer">
          Documents page.
        </Link>
      </div>
      <div className="mt-6">
        <TextInput
          label="FILE NAME"
          className="text-sm"
          value={fileName}
          onChange={(e) => {
            if (e.target.value.match(/^[A-Za-z0-9_\-]*$/)) {
              setFileName(e.target.value);
            }
          }}
        />
        {!fileName && (
          <div className="font-normal text-[0.65rem] text-mars mt-0.5">
            File Name is required
          </div>
        )}
        {fileName && fileName.length < 3 && (
          <div className="font-normal text-[0.65rem] text-mars mt-0.5">
            File Name must be at least 3 characters long
          </div>
        )}
      </div>
      <div className="flex items-center justify-end mt-8">
        <PrimaryButton
          className="flex items-center gap-2 font-bold"
          isDisabled={loading || !fileName || fileName.length < 3}
          onClick={async () => {
            const name = `${fileName}-${new Date().getTime()}`;

            await exportData({
              variables: {
                ...{
                  ...reportTableVariables,
                  sorting: undefined,
                  page: undefined,
                  perPage: undefined,
                },
                exportFileName: name,
              },
            });

            addToast(
              <div>
                <div>Your landing page analytics export has started!</div>
                <div className="mt-3">
                  When your export is done it will live in the Documents page
                  under the name:
                </div>
                <div className="mt-3">{name}.csv</div>
              </div>,
              { appearance: "success", autoDismiss: false },
            );

            prettyLogEventPage({
              domain: "Landing Page Analytics",
              action: "onClick",
              additionalActionInfo: "Export table data",
              data: {
                pageContext,
              },
            });

            onClose();
          }}
        >
          {loading && <Spinner colorClassname="text-white-400" />}
          EXPORT
        </PrimaryButton>
      </div>
    </Modal>
  );
};
